/**
 * Users API client
 */

import { getAPIClient } from './client';

export interface User {
  id: string;
  email: string;
  username: string;
  full_name?: string;
  is_active: boolean;
  is_email_verified: boolean;
  last_login_at?: string;
  created_at: string;
  /** SPARC-009 regional visibility codes (CA, US, IN, … or ALL). */
  country_access?: string[] | null;
}

export interface UserDetail extends User {
  /** password | azure_ad | google — affects opportunity email "From" (SSO uses your mailbox via Power Automate) */
  auth_provider?: string | null;
  tenant_assignments: Array<{
    tenant_id: string;
    tenant_name: string;
    role_id?: string;
    role_name?: string;
    status: string;
  }>;
  project_assignments: Array<{
    project_id: string;
    project_name: string;
    role_id?: string;
    role_name?: string;
    status: string;
  }>;
}

export interface ListUsersResponse {
  users: User[];
  total: number;
}

export interface UpdateUserRequest {
  email?: string;
  username?: string;
  full_name?: string;
  is_active?: boolean;
  country_access?: string[];
}

export interface UpdateMyPasswordRequest {
  current_password: string;
  new_password: string;
}

export interface ResetUserPasswordRequest {
  new_password: string;
}

export interface PasswordActionResponse {
  message: string;
  email?: string;
}

/**
 * List users
 */
export async function listUsers(params?: {
  tenant_id?: string;
  project_id?: string;
  active_only?: boolean;
}): Promise<ListUsersResponse> {
  const client = getAPIClient();
  return client.get<ListUsersResponse>('/users', { params });
}

/**
 * Get user details
 */
export async function getUser(userId: string): Promise<UserDetail> {
  const client = getAPIClient();
  return client.get<UserDetail>(`/users/${userId}`);
}

/**
 * Update user
 */
export async function updateUser(userId: string, request: UpdateUserRequest): Promise<User> {
  const client = getAPIClient();
  return client.patch<User>(`/users/${userId}`, request);
}

/**
 * Delete user (soft delete)
 */
export async function deleteUser(userId: string): Promise<{ message: string; user_id: string }> {
  const client = getAPIClient();
  return client.delete(`/users/${userId}`);
}

/**
 * Get current user profile
 */
export async function getCurrentUserProfile(): Promise<UserDetail> {
  const client = getAPIClient();
  // Use /users/me endpoint (now available in backend)
  return await client.get<UserDetail>('/users/me');
}

/**
 * Update current user's password
 */
export async function updateMyPassword(request: UpdateMyPasswordRequest): Promise<PasswordActionResponse> {
  const client = getAPIClient();
  return client.patch<PasswordActionResponse>('/users/me/password', request);
}

/**
 * Reset another user's password (admin/admin_tech)
 */
export async function resetUserPassword(userId: string, request: ResetUserPasswordRequest): Promise<PasswordActionResponse> {
  const client = getAPIClient();
  return client.post<PasswordActionResponse>(`/users/${userId}/reset-password`, request);
}











